'use client';

import { FormEvent, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Hammer } from 'lucide-react';
import { Circle, Cross, PatternPanel, Squiggle, Square, Triangle } from '@/components/public/decor';

export default function BuildingUpPage() {
  const router = useRouter();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (busy || !code.trim()) return;

    setBusy(true);
    setError('');

    const response = await fetch('/api/preview-unlock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
    });

    if (response.ok) {
      router.refresh();
      return;
    }

    setError('That code did not work.');
    setBusy(false);
  }

  return (
    <div className="theme-playful relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
      <PatternPanel pattern="dots" className="inset-0 opacity-60" />
      <Circle tone="amber" className="-left-24 -top-16 h-72 w-72 opacity-40" />
      <Circle tone="pink" className="-bottom-20 -right-16 h-64 w-64 opacity-30" />
      <Triangle tone="mint" size={44} className="left-[15%] bottom-[18%] rotate-12" />
      <Square tone="accent" className="right-[18%] top-[16%] h-12 w-12 rotate-12 opacity-70" />
      <Cross tone="pink" size={28} className="left-[22%] top-[22%]" />

      <div className="container relative px-4">
        <div className="mx-auto max-w-xl text-center">
          <Image
            src="/logo-full.png"
            alt="Logistr"
            width={200}
            height={50}
            className="mx-auto h-10 w-auto"
            priority
          />

          <div className="mt-10 flex justify-center">
            <span className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-pop-ink bg-accent shadow-pop-lg">
              <Hammer className="h-11 w-11 text-white" strokeWidth={2.5} />
            </span>
          </div>

          <h1 className="mt-10 font-outfit text-4xl font-extrabold leading-tight text-foreground sm:text-5xl">
            Something new is
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">on the way</span>
              <Squiggle tone="pink" width={200} className="absolute -bottom-2 left-0 w-full" />
            </span>
          </h1>

          <p className="mt-7 text-lg text-muted-foreground">
            We&apos;re building something exciting. Check back soon.
          </p>

          <form onSubmit={unlock} className="mx-auto mt-10 max-w-xs">
            <label htmlFor="preview-code" className="sr-only">
              Preview code
            </label>
            <div className="flex gap-2">
              <input
                id="preview-code"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Have a code?"
                className="h-11 flex-1 rounded-full border-2 border-pop-ink bg-white px-4 text-center font-outfit text-base tracking-widest text-foreground outline-none placeholder:tracking-normal placeholder:text-muted-foreground focus:ring-2 focus:ring-accent"
              />
              <button
                type="submit"
                disabled={busy || !code.trim()}
                className="h-11 rounded-full border-2 border-pop-ink bg-accent px-5 font-outfit text-sm font-semibold text-white shadow-pop disabled:opacity-50"
              >
                {busy ? '…' : 'Enter'}
              </button>
            </div>
            {error && (
              <p className="mt-3 text-sm text-accent" role="alert">
                {error}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
